import { logEvent as fsLogEvent, identify as fsIdentify } from "@fullscript/fs-analytics";
import type { Identify } from "@fullscript/fs-analytics/rudderTyper";
import { useEffect, useRef, useCallback } from "react";

import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { useFsAnalyticsUserSharedQuery } from "./FsAnalyticsUser.query.generated";

const toProfileId = (userId: string, role: string): string | null => {
  const normalized = role.toLowerCase();

  if (normalized === "roleless") {
    return null;
  }

  const suffix = normalized === "patient" ? "PATIENT" : "STAFF";

  return `${userId}_${suffix}`;
};

const useFsAnalytics = () => {
  const { isAdminImpersonating, storePlatform, isUnauthenticated } = useSharedGlobalConfig();
  const {
    data: userData,
    loading,
    error,
  } = useFsAnalyticsUserSharedQuery({
    skip: isUnauthenticated,
  });
  const isReady = (!loading && !error) || isUnauthenticated;

  const getContext = useCallback(
    (): Record<string, unknown> => ({
      email: userData?.currentUser?.email,
      role_type: userData?.currentUser?.currentUserRoleType,
      environment: userData?.analyticsEnvironment,
      impersonating: isAdminImpersonating,
      store_platform: storePlatform,
    }),
    [userData, isAdminImpersonating, storePlatform]
  );

  type QueuedEvent = {
    type: "event";
    fn: keyof ReturnType<typeof fsLogEvent>;
    payload?: Record<string, unknown>;
  };
  type QueuedIdentify = { type: "identify"; preparedUserId?: string | null };
  const queue = useRef<(QueuedEvent | QueuedIdentify)[]>([]);

  useEffect(() => {
    if (!isReady || queue.current.length === 0) return;

    const eventHandlers = fsLogEvent();
    const ctx = getContext();
    const currentRoleType = userData?.currentUser?.currentUserRoleType?.trim();

    queue.current.forEach(item => {
      if (item.type === "event") {
        (eventHandlers[item.fn] as (p: Record<string, unknown>) => void)({
          ...ctx,
          ...item.payload,
        });
      } else if (item.type === "identify" && currentRoleType) {
        const userId = (userData?.preparedUserId ?? item.preparedUserId?.trim()) || "";
        if (!userId) return;

        const profileId = toProfileId(userId, currentRoleType);
        const identifyCtx = ctx as unknown as Identify;

        if (profileId) {
          fsIdentify(profileId, { ...identifyCtx, profile_id: profileId });
        } else {
          fsIdentify(identifyCtx);
        }
      }
    });

    queue.current = [];
  }, [isReady, userData, getContext]);

  const logEvent = () => {
    let eventHandlers: ReturnType<typeof fsLogEvent>;
    try {
      eventHandlers = fsLogEvent();
    } catch {
      return new Proxy({} as ReturnType<typeof fsLogEvent>, {
        get: () => () => {},
      });
    }

    return new Proxy(eventHandlers, {
      get: (target, prop) => {
        const originalFn = target[prop as keyof typeof target];
        if (typeof originalFn === "function") {
          return (payload: Record<string, unknown> = {}) => {
            if (isReady) {
              return (originalFn as (p: Record<string, unknown>) => void)({
                ...getContext(),
                ...payload,
              });
            }
            queue.current.push({ type: "event", fn: prop as keyof typeof fsLogEvent, payload });
          };
        }

        return originalFn;
      },
    });
  };

  const identify = (preparedUserId?: string | null) => {
    try {
      if (!isReady) {
        queue.current.push({ type: "identify", preparedUserId });

        return;
      }

      const roleType = userData?.currentUser?.currentUserRoleType?.trim();
      const userId = (userData?.preparedUserId ?? preparedUserId?.trim()) || "";
      if (!roleType || !userId) return;

      const profileId = toProfileId(userId, roleType);
      const ctx = getContext() as unknown as Identify;

      if (profileId) {
        fsIdentify(profileId, { ...ctx, profile_id: profileId });
      } else {
        fsIdentify(ctx);
      }
    } catch {
      // no-op
    }
  };

  return { logEvent, identify, isReady };
};

export { useFsAnalytics };
